import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/budget/model/program/ProgramBudget.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";

import { db } from "../../../firebase.js";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import {
  LineChartWithToggles,
  BudgetInflationChart,
} from "../../../shared/ui/chart/LineChartWithToggles.jsx";

import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";

import { buildProgramRowsFromDoc } from "../../utils/programRowUtils.js";
import { buildColumnsByTableId } from "./programBudgetTables.js";
import {
  buildSummaryColumns,
  buildSummaryRow,
} from "./programBudgetSummary.js";
import { buildSnapshotTable } from "./programBudgetSnapshot.js";

import {
  buildApprovedChartData,
  buildActualChartData,
  buildDiffChartData,
  buildSalaryPositionPairs,
  buildAvgSalaryChartData,
  buildInflationChartData,
} from "../../../shared/ui/chart/charts/programBudgetCharts.js";

import {
  toYearNumber,
  sumSectionTotals,
  buildSnapshotProgramLabel,
  buildDisplayHeading,
  filterZeroSnapshotSections,
  buildApprovedRowsForTable,
  getValidWindowEndingYears,
  collectAvailableYearsFromRows,
  buildYearAvailabilityFromRows,
} from "../../utils/programBudgetUtils.js";

import { BOE_PROGRAM_DOC_IDS } from "../../../shared/constants/programBudget.js";

/**
 * ProgramBudget
 *
 * Generic program budget renderer driven by a configuration object (PROGRAM_CONFIGS).
 *
 * Responsibilities:
 * - Load a single program document from Firestore (e.g., publicSchools/{docId})
 * - Normalize/shape that document into sectioned row arrays via `buildProgramRowsFromDoc`
 * - Build and render one or more `CommonTable` views based on config-defined `tables`
 * - Provide "Snapshot by Year" (PDF-style) view with a selectable window ending year
 * - Render multiple optional line charts (approved, actual, diff, avg salary, inflation)
 * - Optionally compute and display "% of total BoE budget" (requires loading all BoE docs)
 * - Support edit mode by updating local state and bubbling changes to parent via setter
 *
 * Key domain conventions:
 * - Budget years are treated as FY start years (e.g., 2025 => FY 2025–2026)
 * - Approved table has a Proposed fallback injection (see `buildApprovedRowsForTable`)
 *
 * Data dependencies:
 * - Program doc: `{collectionName}/{docId}`
 * - Inflation doc: `common/inflationByYear`
 * - Share-of-total helper doc: `boardOfEd/programBudgetByYear` (stored but not required for all views)
 *
 * @component
 * @param {Object} props
 * @param {string} props.programId
 *   Program identifier used by the parent domain and as a key when bubbling edits.
 * @param {Object} props.config
 *   Configuration describing Firestore doc id, fields, table definitions, and chart behavior.
 * @param {boolean} [props.isEditing=false]
 *   When true, enables inline editing in non-snapshot tables.
 * @param {Function} [props.setProgramData]
 *   Optional setter to bubble program edits to a parent state container (preferred).
 * @param {Function} [props.setBoeData]
 *   Legacy/alternate setter name supported to prevent parent/child mismatches.
 * @param {string} [props.collectionName="publicSchools"]
 *   Firestore collection name to load the program document from.
 *
 * @returns {JSX.Element} Program budget tables and charts for the selected program.
 */
export function ProgramBudget({
  programId,
  config,
  isEditing = false,

  // Support BOTH names to prevent parent/child mismatches from throwing.
  // BoardOfEd currently passes setBoeData; some older code uses setProgramData.
  setProgramData,
  setBoeData,

  collectionName = "publicSchools",
}) {
  const {
    fullName,
    docId,
    fieldDefs = [],
    tables = [],
    inflationSumFields = null,
  } = config || {};

  /**
   * Parent state propagation hook:
   * Prefer `setProgramData` if provided, otherwise fall back to `setBoeData`.
   * When neither is provided, edits remain local to this component.
   *
   * @type {Function|null}
   */
  const bubbleSetProgramData =
    typeof setProgramData === "function"
      ? setProgramData
      : typeof setBoeData === "function"
        ? setBoeData
        : null;

  // ---- Component state ----

  /** @type {[boolean, Function]} Indicates Firestore fetch in progress. */
  const [loading, setLoading] = useState(false);

  const snapshotExcelExportRef = useRef(null);

  /**
   * Sectioned rows keyed by section name (e.g., "Approved", "Actual", "Proposed").
   * Each entry is an array of normalized row objects built from the Firestore doc.
   *
   * @type {[Object<string, Array<Object>>, Function]}
   */
  const [rowsBySection, setRowsBySection] = useState({});

  /**
   * Inflation lookup keyed by fiscal year (start-year semantics).
   * Populated from `common/inflationByYear` (expected shape: { years: { [year]: value } }).
   *
   * @type {[Object<string, number>, Function]}
   */
  const [inflationByYear, setInflationByYear] = useState({});

  /**
   * Raw programBudgetByYear document used for share-of-total analysis.
   * This is loaded for completeness; share computations in this file primarily
   * use the set of BoE program docs for robustness.
   *
   * @type {[Object|null, Function]}
   */
  const [programBudgetDoc, setProgramBudgetDoc] = useState(null);

  /**
   * Selected year for the snapshot "window ending year".
   * This value is chosen from `snapshotYearOptions`.
   *
   * @type {[number|null, Function]}
   */
  const [snapshotYear, setSnapshotYear] = useSharedFilter("ProgramBudget.jsx:ProgramBudget:snapshotYear:" + [programId].join(":"), null);

  /**
   * Selected year for the "summary" calculations (computed but not currently rendered here).
   *
   * @type {[number|null, Function]}
   */
  const [summaryYear, setSummaryYear] = useSharedFilter("ProgramBudget.jsx:ProgramBudget:summaryYear:" + [programId].join(":"), null);

  /** @type {[boolean, Function]} UI toggle for snapshot table visibility. */
  const [showSnapshotTable, setShowSnapshotTable] = useState(true);

  /** @type {[boolean, Function]} UI toggles for optional chart panels. */
  const [showApprovedChart, setShowApprovedChart] = useState(false);
  const [showActualChart, setShowActualChart] = useState(false);
  const [showDiffChart, setShowDiffChart] = useState(false);
  const [showAvgSalaryChart, setShowAvgSalaryChart] = useState(false);
  const [showInflationChart, setShowInflationChart] = useState(false);
  const [showShareChart, setShowShareChart] = useState(false);

  /**
   * Collection of all BoE program docs keyed by doc id.
   * Loaded only when `collectionName === "publicSchools"` so we can compute
   * "% of total" reliably across all programs.
   *
   * @type {[Object<string, Object>|null, Function]}
   */
  const [allBoeProgramDocs, setAllBoeProgramDocs] = useState(null);

  /**
   * Selects which budget slice to use when computing share-of-total.
   * Common values: "Approved", "Actual", "Proposed" (matching section keys).
   *
   * @type {[string, Function]}
   */
  const [shareBudgetType, setShareBudgetType] = useSharedFilter("ProgramBudget.jsx:ProgramBudget:shareBudgetType:" + [programId].join(":"), "Approved");

  /**
   * Per-table UI visibility state keyed by table id.
   * Defaults to all tables visible.
   *
   * @type {[Object<string, boolean>, Function]}
   */
  const [tableVisibility, setTableVisibility] = useState(() => {
    const initial = {};
    for (const t of tables) initial[t.id] = true;
    return initial;
  });

  /**
   * Display heading used in headers/titles (program name normalization).
   */
  const displayHeading = useMemo(
    () => buildDisplayHeading({ docId, programId, fullName }),
    [docId, programId, fullName],
  );

  // ---- Derived config maps ----

  /**
   * Map of field definition objects keyed by field key.
   * Used by table/row builders for consistent formatting and labeling.
   *
   * @type {Map<string, Object>}
   */
  const fieldDefMap = useMemo(() => {
    const map = new Map();
    for (const fd of fieldDefs) map.set(fd.key, fd);
    return map;
  }, [fieldDefs]);

  /**
   * Distinct section keys referenced by tables in config.
   * Typical keys: "Approved", "Actual", "Proposed".
   *
   * @type {string[]}
   */
  const sectionKeys = useMemo(
    () => Array.from(new Set(tables.map((t) => t.sectionKey))),
    [tables],
  );

  /**
   * Column definitions for each table, built from config and field definitions.
   * Keyed by `table.id`.
   *
   * @type {Object<string, Array<Object>>}
   */
  const columnsByTableId = useMemo(
    () => buildColumnsByTableId(tables, fieldDefMap),
    [tables, fieldDefMap],
  );

  // ---- Load Firestore data ----

  /**
   * Loads:
   * - The selected program document (configured by docId/collectionName)
   * - Inflation data (common/inflationByYear)
   * - programBudgetByYear (boardOfEd/programBudgetByYear)
   * - If the collection is publicSchools, also loads all BoE program docs
   *   to compute share-of-total chart.
   *
   * Uses an `isMounted` guard to prevent state updates after unmount.
   */
  useEffect(() => {
    if (!docId) return;
    let isMounted = true;

    async function load() {
      setLoading(true);
      try {
        const [progSnap, inflSnap, programBudgetSnap] = await Promise.all([
          getDoc(doc(db, collectionName, docId)),
          getDoc(doc(db, "common", "inflationByYear")),
          getDoc(doc(db, "boardOfEd", "programBudgetByYear")),
        ]);

        // Program rows
        if (!progSnap.exists()) {
          console.warn(`⚠️ ${collectionName}/${docId} does not exist`);
          if (isMounted) setRowsBySection({});
        } else {
          const data = progSnap.data();

          const next = {};
          for (const sectionKey of sectionKeys) {
            next[sectionKey] = buildProgramRowsFromDoc(
              data,
              fieldDefs,
              sectionKey,
            );
          }

          // Always include Proposed for snapshot + fallback logic
          next["Proposed"] = buildProgramRowsFromDoc(
            data,
            fieldDefs,
            "Proposed",
          );

          if (isMounted) {
            setRowsBySection(next);

            if (bubbleSetProgramData) {
              bubbleSetProgramData((prev) => ({
                ...prev,
                [programId]: {
                  fullName: data.fullName || fullName || programId,
                  ...next,
                },
              }));
            }
          }
        }

        // Load all BoE program docs so we can compute "% of total" reliably.
        if (collectionName === "publicSchools") {
          const snaps = await Promise.all(
            BOE_PROGRAM_DOC_IDS.map((id) =>
              getDoc(doc(db, "publicSchools", id)),
            ),
          );

          const docsById = {};
          snaps.forEach((s, idx) => {
            if (s.exists()) docsById[BOE_PROGRAM_DOC_IDS[idx]] = s.data();
          });

          if (isMounted) setAllBoeProgramDocs(docsById);
        }

        // Inflation
        if (inflSnap.exists() && isMounted) {
          const inflData = inflSnap.data();
          setInflationByYear(inflData?.years || {});
        }

        // ProgramBudgetByYear (share of total)
        if (programBudgetSnap.exists() && isMounted) {
          setProgramBudgetDoc(programBudgetSnap.data());
        }
      } catch (err) {
        console.error(`Error loading program ${programId} data:`, err);
        if (isMounted) {
          setRowsBySection({});
          setInflationByYear({});
          setProgramBudgetDoc(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [
    docId,
    sectionKeys,
    fieldDefs,
    fullName,
    programId,
    bubbleSetProgramData,
    collectionName,
  ]);

  // ---- Convenience aliases ----

  /** @type {Array<Object>} Approved rows (may be empty). */
  const approvedRows = rowsBySection["Approved"] || [];
  /** @type {Array<Object>} Actual rows (may be empty). */
  const actualRows = rowsBySection["Actual"] || [];
  /** @type {Array<Object>} Proposed rows (may be empty). */
  const proposedRows = rowsBySection["Proposed"] || [];

  /**
   * Approved-table-only fallback:
   * Injects Proposed rows where Approved data is missing (table-only view).
   *
   * This preserves accuracy for Approved table rendering while maintaining
   * separate "Proposed" semantics for snapshot and chart logic.
   */
  const approvedRowsForTable = useMemo(
    () => buildApprovedRowsForTable(approvedRows, proposedRows),
    [approvedRows, proposedRows],
  );

  // ---- Years for snapshot ----

  /**
   * Collects all distinct fiscal start years appearing in any section.
   *
   * @type {number[]}
   */
  const allAvailableYears = useMemo(
    () => collectAvailableYearsFromRows(approvedRows, actualRows, proposedRows),
    [approvedRows, actualRows, proposedRows],
  );

  /**
   * Map describing which section types exist per year, used to validate
   * snapshot "window ending year" options.
   *
   * @type {Map<number, {hasActual:boolean, hasApproved:boolean, hasProposed:boolean}>}
   */
  const yearAvailFromRows = useMemo(
    () => buildYearAvailabilityFromRows(approvedRows, actualRows, proposedRows),
    [approvedRows, actualRows, proposedRows],
  );

  /**
   * Valid snapshot window ending years, derived from row presence rules.
   */
  const snapshotYearOptions = useMemo(() => {
    if (!allAvailableYears.length) return [];
    return getValidWindowEndingYears(allAvailableYears, yearAvailFromRows);
  }, [allAvailableYears, yearAvailFromRows]);

  /**
   * Initializes/repairs snapshotYear selection when available options change.
   * Defaults to the most recent valid year.
   */
  useEffect(() => {
    if (!snapshotYearOptions.length) return;
    setSnapshotYear.initialize((prev) =>
      prev && snapshotYearOptions.includes(prev)
        ? prev
        : snapshotYearOptions[snapshotYearOptions.length - 1],
    );
  }, [snapshotYearOptions]);

  // ---- Share of total ----

  /**
   * Computes the program's share of total BoE budget for each year as a percent.
   * Uses all BoE program docs and sums section totals per year.
   *
   * Output records are in label/value form for chart consumption.
   */
  const shareOfTotalData = useMemo(() => {
    if (collectionName !== "publicSchools") return [];
    if (!allBoeProgramDocs || !allBoeProgramDocs[docId]) return [];

    const budgetType = shareBudgetType;
    const yearSet = new Set();

    Object.values(allBoeProgramDocs).forEach((docData) => {
      Object.keys(docData || {}).forEach((yKey) => {
        const y = toYearNumber(yKey);
        if (y) yearSet.add(y);
      });
    });

    const years = [...yearSet].sort((a, b) => a - b);

    return years
      .map((y) => {
        const yKey = String(y);

        let totalAll = 0;
        let sawAll = false;

        Object.values(allBoeProgramDocs).forEach((docData) => {
          const section = docData?.[yKey]?.[budgetType];
          const t = sumSectionTotals(section);
          if (typeof t === "number" && Number.isFinite(t)) {
            totalAll += t;
            sawAll = true;
          }
        });

        const thisSection = allBoeProgramDocs?.[docId]?.[yKey]?.[budgetType];
        const programTotal = sumSectionTotals(thisSection);

        if (!sawAll || !Number.isFinite(totalAll) || totalAll <= 0) return null;
        if (!Number.isFinite(programTotal)) return null;

        return {
          label: formatFiscalYearFromStart(y),
          percentOfTotal: (programTotal / totalAll) * 100,
        };
      })
      .filter(Boolean);
  }, [collectionName, allBoeProgramDocs, docId, shareBudgetType]);

  /**
   * Converts share-of-total data into chart-ready labels/series.
   */
  const shareChart = useMemo(() => {
    if (!shareOfTotalData.length) return { labels: [], series: [] };

    return {
      labels: shareOfTotalData.map((d) => d.label),
      series: [
        {
          key: "percentOfTotal",
          label: "% of Total BoE Budget",
          data: shareOfTotalData.map((d) => d.percentOfTotal),
          group: "other",
          defaultVisible: true,
          valueType: "percent",
        },
      ],
    };
  }, [shareOfTotalData]);

  // ---- Summary table inputs (computed for future use) ----

  /**
   * Currency-valued field keys, used to compute summary aggregates.
   */
  const sumCurrencyKeys = useMemo(
    () =>
      fieldDefs.filter((fd) => fd.valueType === "currency").map((fd) => fd.key),
    [fieldDefs],
  );

  /**
   * Lookup maps keyed by year for quick access in summary computations.
   */
  const approvedByYear = useMemo(() => {
    const map = new Map();
    approvedRows.forEach(
      (row) => typeof row.year === "number" && map.set(row.year, row),
    );
    return map;
  }, [approvedRows]);

  const actualByYear = useMemo(() => {
    const map = new Map();
    actualRows.forEach(
      (row) => typeof row.year === "number" && map.set(row.year, row),
    );
    return map;
  }, [actualRows]);

  const allYears = useMemo(() => {
    const set = new Set([...approvedByYear.keys(), ...actualByYear.keys()]);
    return [...set].sort((a, b) => a - b);
  }, [approvedByYear, actualByYear]);

  /**
   * Valid summary years require a contiguous 3-year window.
   */
  const summaryYearOptions = useMemo(() => {
    if (allYears.length < 3) return [];
    return allYears.filter(
      (y) => allYears.includes(y - 1) && allYears.includes(y - 2),
    );
  }, [allYears]);

  /**
   * Initializes/repairs summaryYear selection when options change.
   * Defaults to the most recent valid year.
   */
  useEffect(() => {
    if (!summaryYearOptions.length) return;
    setSummaryYear.initialize((prev) =>
      prev && summaryYearOptions.includes(prev)
        ? prev
        : summaryYearOptions[summaryYearOptions.length - 1],
    );
  }, [summaryYearOptions]);

  const proposedByYear = useMemo(() => {
    const map = new Map();
    proposedRows.forEach(
      (row) => typeof row.year === "number" && map.set(row.year, row),
    );
    return map;
  }, [proposedRows]);

  /**
   * Summary columns (memoized) computed for side-effect-free derivation.
   * Currently not rendered in this component, but preserved for future use.
   */
  useMemo(
    () => buildSummaryColumns({ summaryYear, approvedByYear, proposedByYear }),
    [summaryYear, approvedByYear, proposedByYear],
  );

  /**
   * Summary row (memoized) computed for side-effect-free derivation.
   * Currently not rendered in this component, but preserved for future use.
   */
  useMemo(
    () =>
      buildSummaryRow({
        summaryYear,
        approvedByYear,
        actualByYear,
        sumCurrencyKeys,
        fullName,
        programId,
      }),
    [
      summaryYear,
      approvedByYear,
      actualByYear,
      sumCurrencyKeys,
      fullName,
      programId,
    ],
  );

  // ---- Snapshot (PDF-style detail table) ----

  /**
   * Snapshot columns/rows are constructed from the selected snapshotYear and
   * the available window-ending years.
   */
  const { snapshotColumns, snapshotRows } = useMemo(
    () =>
      buildSnapshotTable({
        snapshotYear,
        availableYears: snapshotYearOptions,
        fieldDefs,
        tables,
        approvedRows,
        actualRows,
        proposedRows,
        fieldDefMap,
        programLabel: buildSnapshotProgramLabel({ docId, programId, fullName }),
      }),
    [
      snapshotYear,
      snapshotYearOptions,
      fieldDefs,
      tables,
      approvedRows,
      actualRows,
      proposedRows,
      fieldDefMap,
      docId,
      fullName,
      programId,
    ],
  );

  /**
   * Removes sections that are all zeros and removes subtotal rows for display clarity.
   */
  const filteredSnapshotRows = useMemo(() => {
    const rows = filterZeroSnapshotSections(snapshotRows, snapshotColumns);
    return rows.filter((r) => r?.rowType !== "subtotal");
  }, [snapshotRows, snapshotColumns]);

  // ---- Edit handler ----

  /**
   * Handles inline edit changes for editable tables.
   *
   * Editing model:
   * - Updates local `rowsBySection` immutably
   * - Treats empty/invalid input as null
   * - If a parent setter is provided, bubbles the updated rows keyed by programId
   *
   * @param {string} sectionKey - Section being edited (e.g. "Approved", "Actual", "Proposed")
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event from CommonTable cell editor
   * @param {string} rowLabel - Unique label used to locate the row being edited
   * @param {string} colKey - Field key being edited
   * @returns {void}
   */
  const handleCellChange = (sectionKey, e, rowLabel, colKey) => {
    const value = e.target.value;
    const numericValue =
      value === "" ? null : isNaN(Number(value)) ? null : Number(value);

    setRowsBySection((prev) => {
      const sectionRows = prev[sectionKey] || [];
      const updatedSectionRows = sectionRows.map((row) =>
        row.label === rowLabel ? { ...row, [colKey]: numericValue } : row,
      );

      const next = { ...prev, [sectionKey]: updatedSectionRows };

      if (bubbleSetProgramData) {
        bubbleSetProgramData((prevBoe) => ({
          ...prevBoe,
          [programId]: {
            ...(prevBoe[programId] || { fullName: fullName || programId }),
            ...next,
          },
        }));
      }

      return next;
    });
  };

  // ---- Charts ----

  /**
   * Chart series builders return chart-ready label arrays and series configs.
   * Visibility toggles are purely UI-level state.
   */
  const { approvedChartLabels, approvedChartSeries } = useMemo(
    () => buildApprovedChartData(approvedRows, proposedRows, fieldDefs),
    [approvedRows, proposedRows, fieldDefs],
  );

  const { actualChartLabels, actualChartSeries } = useMemo(
    () => buildActualChartData(actualRows, fieldDefs),
    [actualRows, fieldDefs],
  );

  const { diffChartLabels, diffChartSeries, diffSeriesGroups } = useMemo(
    () => buildDiffChartData(approvedRows, actualRows, fieldDefs),
    [approvedRows, actualRows, fieldDefs],
  );

  /**
   * Salary/position pairs identify which fields can be combined to derive
   * average salary estimates over time.
   */
  const salaryPositionPairs = useMemo(
    () => buildSalaryPositionPairs(fieldDefs),
    [fieldDefs],
  );

  const { avgSalaryLabels, avgSalarySeries } = useMemo(
    () =>
      buildAvgSalaryChartData(approvedRows, actualRows, salaryPositionPairs),
    [approvedRows, actualRows, salaryPositionPairs],
  );

  const {
    inflationLabels,
    approvedInflationSeries,
    actualInflationSeries,
    inflationBaselineSeries,
    inflationBaselineSeriesForActual,
  } = useMemo(
    () =>
      buildInflationChartData(
        approvedRows,
        actualRows,
        proposedRows,
        inflationByYear,
        fieldDefs,
        inflationSumFields,
      ),
    [
      approvedRows,
      actualRows,
      proposedRows,
      inflationByYear,
      fieldDefs,
      inflationSumFields,
    ],
  );

  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading {programId} {fullName}…
      </div>
    );
  }

  return (
    <TableSearchScope>
    <div className="space-y-8">
      {/* Snapshot-by-year table (PDF-style) */}
      {snapshotColumns.length > 0 && filteredSnapshotRows.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showSnapshotTable}
            onToggle={() => setShowSnapshotTable((prev) => !prev)}
            title={`${displayHeading} – Snapshot by Year`}
            ariaLabel="Toggle snapshot table"
            controlsId={`${docId || programId}-snapshot-table`}
            rightSlot={
              <div
                className="ml-auto flex items-center gap-3 pr-2 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Download snapshot */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center bg-transparent border-0 p-0 m-0 text-gray-700 hover:text-gray-900 focus:outline-none leading-none"
                  style={{ lineHeight: 1 }}
                  title="Download snapshot to Excel"
                  aria-label="Download snapshot to Excel"
                  onClick={(e) => {
                    e.stopPropagation();
                    snapshotExcelExportRef.current?.exportToExcel?.();
                  }}
                >
                  <span className="relative top-[1px] text-base">💾</span>
                </button>

                {snapshotYearOptions.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {showSnapshotTable && <SectionTableSearch />}
                    <span className="font-semibold text-gray-700 whitespace-nowrap">
                      Current Year
                    </span>

                    <select
                      className="border rounded px-2 py-1 text-xs bg-white"
                      value={snapshotYear || ""}
                      onChange={(e) =>
                        setSnapshotYear(
                          e.target.value
                            ? Number(e.target.value)
                            : snapshotYear,
                        )
                      }
                    >
                      {snapshotYearOptions.map((year) => (
                        <option key={year} value={year}>
                          {formatFiscalYearFromStart(year)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            }
          />

          {showSnapshotTable && (
            <div id={`${docId || programId}-snapshot-table`}>
              <CommonTable
                excelExportRef={snapshotExcelExportRef}
                excelFilename={`${docId || programId}-snapshot-${snapshotYear || "latest"}.xlsx`}
                excelSheetName="Snapshot"
                excelTitle={`${displayHeading} – Snapshot by Year (${formatFiscalYearFromStart(
                  snapshotYear || 0,
                )})`}
                title={null}
                columns={snapshotColumns}
                data={filteredSnapshotRows}
                isEditing={false}
                headerColor="bg-[#e5eef9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />
            </div>
          )}
        </div>
      )}

      {/* TABLES */}
      {tables.map((table) => {
        const rows =
          table.sectionKey === "Approved"
            ? approvedRowsForTable // <-- Proposed fallback row injected here
            : rowsBySection[table.sectionKey] || [];

        const cols = columnsByTableId[table.id] || [];
        const isVisible = tableVisibility[table.id] ?? true;

        const tableControlsId = `${docId || programId}-${table.id}-table`;

        return (
          <div key={table.id} className="space-y-2">
            <HeaderToggleButton
              isOpen={isVisible}
              onToggle={() =>
                setTableVisibility((prev) => ({
                  ...prev,
                  [table.id]: !isVisible,
                }))
              }
              title={table.title}
              ariaLabel={`Toggle ${table.title}`}
              controlsId={tableControlsId}
            />

            {isVisible && (
              <div id={tableControlsId}>
                <CommonTable
                  title={null}
                  columns={cols}
                  data={rows}
                  isEditing={isEditing}
                  onChange={(e, rowLabel, colKey) =>
                    handleCellChange(table.sectionKey, e, rowLabel, colKey)
                  }
                  headerColor="bg-[#dbead7]"
                  showAggregateTotal={false}
                  defaultSortField="year"
                  defaultSortOrder="asc"
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Approved Budget Chart */}
      {approvedChartLabels.length > 0 && approvedChartSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showApprovedChart}
            onToggle={() => setShowApprovedChart((prev) => !prev)}
            title={`${displayHeading} – Approved Budget`}
            ariaLabel="Toggle approved budget chart"
            controlsId={`${docId || programId}-approved-chart`}
          />

          {showApprovedChart && (
            <div id={`${docId || programId}-approved-chart`}>
              <p className="text-xs text-gray-600 mb-1">
                This chart shows the approved budget for this program by fiscal
                year, with one line per major cost category.
              </p>

              <LineChartWithToggles
                labels={approvedChartLabels}
                series={approvedChartSeries}
                showTotalWhenNoneSelected={true}
                totalLabel="Total Approved Budget"
                proposedRenderMode="merge"
              />
            </div>
          )}
        </div>
      )}

      {/* Actual Spending Chart */}
      {actualChartLabels.length > 0 && actualChartSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showActualChart}
            onToggle={() => setShowActualChart((prev) => !prev)}
            title={`${displayHeading} – Actual Spending`}
            ariaLabel="Toggle actual budget chart"
            controlsId={`${docId || programId}-actual-chart`}
          />

          {showActualChart && (
            <div id={`${docId || programId}-actual-chart`}>
              <p className="text-xs text-gray-600 mb-1">
                This chart shows what the program actually spent each year.
              </p>

              <LineChartWithToggles
                labels={actualChartLabels}
                series={actualChartSeries}
                showTotalWhenNoneSelected={true}
                totalLabel="Total Actual Spending"
              />
            </div>
          )}
        </div>
      )}

      {/* Actual Spending vs Approved Budget Budget Chart */}
      {diffChartLabels.length > 0 && diffChartSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showDiffChart}
            onToggle={() => setShowDiffChart((prev) => !prev)}
            title={`${displayHeading} – Actual Spending vs Approved Budget`}
            ariaLabel="Toggle actual vs approved chart"
            controlsId={`${docId || programId}-diff-chart`}
          />

          {showDiffChart && (
            <div id={`${docId || programId}-diff-chart`}>
              <p className="text-xs text-gray-600 mb-1">
                This chart compares actual spending to the approved budget.
              </p>

              <LineChartWithToggles
                labels={diffChartLabels}
                series={diffChartSeries}
                groups={diffSeriesGroups}
              />
            </div>
          )}
        </div>
      )}

      {/* Average Salary Chart */}
      {avgSalaryLabels.length > 0 && avgSalarySeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showAvgSalaryChart}
            onToggle={() => setShowAvgSalaryChart((prev) => !prev)}
            title={`${displayHeading} – Average Salary`}
            ariaLabel="Toggle average salary chart"
            controlsId={`${docId || programId}-avg-salary-chart`}
          />

          {showAvgSalaryChart && (
            <div id={`${docId || programId}-avg-salary-chart`}>
              <p className="text-xs text-gray-600 mb-1">
                This chart estimates the average salary per FTE over time.
              </p>

              <LineChartWithToggles
                labels={avgSalaryLabels}
                series={avgSalarySeries}
              />
            </div>
          )}
        </div>
      )}

      {/* Budget vs Inflation Chart */}
      {inflationLabels.length > 0 &&
        (approvedInflationSeries.length > 0 ||
          actualInflationSeries.length > 0) && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showInflationChart}
              onToggle={() => setShowInflationChart((prev) => !prev)}
              title={`${displayHeading} – Budget vs Inflation`}
              ariaLabel="Toggle budget vs inflation chart"
              controlsId={`${docId || programId}-inflation-chart`}
            />

            {showInflationChart && (
              <div id={`${docId || programId}-inflation-chart`}>
                <p className="text-xs text-gray-600 mb-1">
                  This chart compares the program&apos;s budget to an inflation
                  baseline.
                </p>

                <BudgetInflationChart
                  labels={inflationLabels}
                  approvedData={approvedInflationSeries}
                  actualData={actualInflationSeries}
                  inflationData={inflationBaselineSeries}
                  inflationDataForActual={inflationBaselineSeriesForActual}
                />
              </div>
            )}
          </div>
        )}

      {/* % of Total BoE Budget Chart */}
      {shareChart.labels.length > 0 && shareChart.series.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showShareChart}
            onToggle={() => setShowShareChart((prev) => !prev)}
            title={`${displayHeading} – % of Total BoE Budget`}
            ariaLabel="Toggle program share of total budget chart"
            controlsId={`${docId || programId}-share-chart`}
          />

          {showShareChart && (
            <div id={`${docId || programId}-share-chart`}>
              <p className="text-xs text-gray-600 mb-1">
                This chart shows what percentage of the total BoE budget is
                allocated to this program.
              </p>

              <LineChartWithToggles
                labels={shareChart.labels}
                series={shareChart.series}
                showSeriesToggles={false}
                showChartTypeSelector={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}
